import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * "How it Works" button + modal explaining how to use BidConvey.
 *
 * Content is taken verbatim from "How to Use BidConvey.pdf" and is keyed
 * by `audience` so the buyer portal shows the buyer steps and the seller
 * portal shows the seller steps. Self-contained (no external dialog
 * dependency) so it can be dropped into any portal regardless of its UI kit.
 *
 * The overlay is rendered through a portal to `document.body`. The trigger
 * usually lives inside a sticky header that uses `backdrop-blur`, and a
 * `backdrop-filter` establishes a containing block for fixed-position
 * descendants — without the portal the `fixed inset-0` overlay would be
 * positioned/clipped relative to that header box instead of the viewport.
 */

const CONTENT = {
  buyer: {
    title: "How BidConvey Works — for Buyers",
    steps: [
      {
        heading: "Step 1: Review the Offer to Purchase",
        body: [
          "Locate the clause that deals with the appointment of the transfer conveyancer. In most standard agreements, the seller is given the contractual right to appoint the conveyancer.",
        ],
      },
      {
        heading: "Step 2: Remove the Conveyancer Nomination Clause",
        body: ["Delete the clause and initial next to the deletion."],
      },
      {
        heading: "Step 3: Add the BidConvey Condition",
        body: [
          "In the “Other Conditions” or “Special Conditions” section of the Offer to Purchase, insert the following wording:",
          "“Transfer conveyancer to be appointed using the BidConvey process – www.bidconvey.com.”",
        ],
      },
      {
        heading: "Step 4: Register After Acceptance",
        body: [
          "Once the seller has accepted your offer, register on BidConvey (www.bidconvey.com) and follow the guided process.",
        ],
      },
      {
        heading: "Step 5: Choice",
        body: [
          "Once the seller has shortlisted the proposals, you will be able to choose a conveyancer.",
        ],
      },
    ],
    closing:
      "By adding one simple condition to your Offer to Purchase, you can use BidConvey to bring transparency, choice, and inclusion to the conveyancer selection process.",
  },
  seller: {
    title: "How BidConvey Works — for Sellers",
    steps: [
      {
        heading: "Step 1: Get Your BidConvey Reference Number",
        body: [
          "Once the Offer to Purchase has been accepted, your estate agent will provide you with a BidConvey reference number. If the sale is concluded without an estate agent, the reference number will be sent directly to you.",
        ],
      },
      {
        heading: "Step 2: Log In and Shortlist Conveyancers",
        body: [
          "Register then Login using the provided reference number (make sure that the reference number is inserted exactly as provided, without any spacing either before or after).",
          "You will now be able to review the 5 conveyancing proposals and shortlist 3 of them.",
        ],
      },
      {
        heading: "Step 3: Wait for the Buyer's Choice",
        body: [
          "The shortlist will be made available to the buyer immediately once you have shortlisted.",
        ],
      },
    ],
    closing: "",
  },
};

export function HowItWorks({ audience = "buyer", label = "How it Works", className = "" }) {
  const [open, setOpen] = useState(false);
  const content = CONTENT[audience] || CONTENT.buyer;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ||
          "inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        }
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {label}
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label={content.title}
          onClick={() => setOpen(false)}
        >
          <div
            className="my-8 w-full max-w-2xl rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">{content.title}</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
              {content.steps.map((step) => (
                <div key={step.heading}>
                  <h3 className="text-sm font-semibold text-gray-900">{step.heading}</h3>
                  {step.body.map((para, i) => (
                    <p key={i} className="mt-1 text-sm leading-relaxed text-gray-600">
                      {para}
                    </p>
                  ))}
                </div>
              ))}

              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-900">That&rsquo;s It</p>
                {content.closing && (
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">{content.closing}</p>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
